import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core'

export type WakeNotice = {
  id: string
  detectedAt: number
  stage: 'recording' | 'sending' | 'sent' | 'ready' | 'error'
  targetStatus: 'pending' | 'found' | 'none'
  targetName: string | null
  route: string | null
  error: string | null
  opened: boolean
}
type WakeCapture = { id: string; status: string; error: string | null; promptSent: boolean | null }
export type VoiceControlState = {
  supported: boolean
  feedbackReady: boolean
  enabled: boolean
  running: boolean
  phase: string
  error: string | null
  pending: number
  notice: WakeNotice | null
  capture: WakeCapture | null
}

type WakePlugin = {
  getState(): Promise<unknown>
  enable(): Promise<unknown>
  disable(): Promise<unknown>
  suspend(): Promise<unknown>
  resume(): Promise<unknown>
  recoverRecording(): Promise<unknown>
  addListener(event: 'stateChange' | 'conversationReady' | 'wakeDetected', listener: (value: unknown) => void): Promise<PluginListenerHandle>
}

const wake = registerPlugin<WakePlugin>('AgentChatWake')
export function isWakeVoiceAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('AgentChatWake')
}
let snapshot: VoiceControlState = {
  supported: isWakeVoiceAvailable(), feedbackReady: false, enabled: false, running: false, phase: 'off', error: null, pending: 0, notice: null, capture: null,
}
const subscribers = new Set<() => void>()
function update(values: Partial<VoiceControlState>) {
  snapshot = { ...snapshot, ...values }
  for (const subscriber of subscribers) subscriber()
}
function acceptState(value: unknown) {
  if (typeof value !== 'object' || value === null || !('enabled' in value) || typeof value.enabled !== 'boolean'
    || !('running' in value) || typeof value.running !== 'boolean' || !('phase' in value) || typeof value.phase !== 'string') {
    throw new Error('The Android voice service returned an unreadable state. Install the latest app update.')
  }
  const capture: WakeCapture | null = 'capture_id' in value && typeof value.capture_id === 'string' && UUID.test(value.capture_id)
    && 'capture_status' in value && typeof value.capture_status === 'string' ? {
      id: value.capture_id, status: value.capture_status,
      error: 'capture_error' in value && typeof value.capture_error === 'string' && value.capture_error ? value.capture_error : null,
      promptSent: 'capture_sent' in value && typeof value.capture_sent === 'boolean' ? value.capture_sent : null,
    } : null
  update({ capture, supported: true, feedbackReady: 'wakeFeedbackVersion' in value && typeof value.wakeFeedbackVersion === 'number' && value.wakeFeedbackVersion >= 1 && 'destinationMode' in value && value.destinationMode === 'first-free-lane', enabled: value.enabled, running: value.running, phase: value.phase,
    error: 'error' in value && typeof value.error === 'string' && value.error ? value.error : null,
    pending: 'pending' in value && typeof value.pending === 'number' && Number.isFinite(value.pending) ? Math.max(0, value.pending) : 0 })
}
function reconcileWakeNotice(): void {
  const notice = snapshot.notice
  if (!notice || notice.stage === 'sent' || notice.stage === 'ready') return
  const capture = snapshot.capture
  const stopped = !snapshot.enabled || snapshot.phase === 'off' || snapshot.phase === 'paused' || snapshot.phase === 'error'
  if (capture?.id === notice.id) {
    if (capture.status === 'sent') update({ notice: { ...notice, stage: capture.promptSent === true ? 'sent' : 'ready', error: null } })
    else if (capture.status === 'held' || capture.status === 'auth') update({ notice: { ...notice, stage: 'error', error: capture.error || (capture.status === 'auth' ? 'Sign in to AgentChat to send your saved recording.' : 'Recording saved on your phone. It was not sent.') } })
    else if (capture.status === 'queued') update({ notice: { ...notice, stage: 'sending', error: capture.error } })
    else if (capture.status === 'recording') update({ notice: { ...notice, stage: stopped ? 'error' : 'recording', error: stopped ? snapshot.error || 'Recording stopped. Any captured audio is retained on your phone.' : null } })
  } else if (notice.stage === 'recording' && stopped) update({ notice: { ...notice, stage: 'error', error: snapshot.error || 'Recording stopped. Any captured audio is retained on your phone.' } })
}
export function dismissWakeNotice(id: string): void {
  if (snapshot.notice?.id === id) update({ notice: null })
}
export const getVoiceControlState = () => snapshot
export function subscribeVoiceControl(subscriber: () => void): () => void {
  subscribers.add(subscriber)
  return () => { subscribers.delete(subscriber) }
}
export async function refreshVoiceControl(): Promise<void> {
  if (!isWakeVoiceAvailable()) { update({ supported: false }); return }
  acceptState(await wake.getState())
  reconcileWakeNotice()
}
export async function setVoiceControlEnabled(enabled: boolean): Promise<void> {
  if (!isWakeVoiceAvailable()) throw new Error('Screen-off voice control needs the latest AgentChat Android app.')
  if (enabled) {
    await refreshVoiceControl()
    if (!snapshot.feedbackReady) throw new Error('Install the latest Android update for instant wake confirmation and free-lane routing.')
  }
  acceptState(await (enabled ? wake.enable() : wake.disable()))
  reconcileWakeNotice()
  nativePaused = false
  if (enabled && microphoneUsers > 0) await syncMicrophone()
}
export async function recoverVoiceRecording(): Promise<void> {
  if (!isWakeVoiceAvailable()) throw new Error('Install the latest AgentChat Android app to recover recordings.')
  await wake.recoverRecording()
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const LANE_ROUTE = /^\/chats\/pwa-userdriven-(?:claude(?:-channel)?|codex|cursor|gemini)-[0-9]+$/
const FREE_LANES_ROUTE = '/chats?tab=lanes&filter=free'
export function wakeConversationRoute(value: unknown): string | null {
  if (typeof value !== 'object' || value === null || !('url' in value) || typeof value.url !== 'string') return null
  if (LANE_ROUTE.test(value.url) || value.url === FREE_LANES_ROUTE) return value.url
  if (!value.url.startsWith('/chats/pwa-auto-code-codex?')) return null
  const params = new URLSearchParams(value.url.split('?')[1])
  const id = params.get('topic_id')
  if (!id || !UUID.test(id)) return null
  return `/chats/pwa-auto-code-codex?tab=code&chat_id=pwa-auto-code-codex&topic_id=${id}`
}
function acceptWake(value: unknown): void {
  if (typeof value !== 'object' || value === null || !('capture_id' in value) || typeof value.capture_id !== 'string'
    || !UUID.test(value.capture_id) || !('detected_at_ms' in value) || typeof value.detected_at_ms !== 'number'
    || !Number.isFinite(value.detected_at_ms)) return
  const age = Date.now() - value.detected_at_ms
  if (age < -5000 || age > 30_000 || snapshot.notice?.id === value.capture_id) return
  update({ enabled: true, running: true, phase: 'recording', notice: { id: value.capture_id, detectedAt: value.detected_at_ms, stage: 'recording',
    targetStatus: 'pending', targetName: null, route: null, error: null, opened: false } })
}
function acceptConversation(value: unknown, onConversation: (route: string) => void): void {
  if (typeof value !== 'object' || value === null || !('capture_id' in value) || value.capture_id !== snapshot.notice?.id) return
  const notice = snapshot.notice
  if (!notice) return
  const route = wakeConversationRoute(value)
  if (!route) return
  const none = 'status' in value && value.status === 'none'
  if (notice.targetStatus === 'found' && (none || notice.route !== route)) return
  const sent = 'sent' in value && value.sent === true
  const name = 'display_name' in value && typeof value.display_name === 'string' ? value.display_name : null
  const open = snapshot.enabled && !none && !sent && (notice.stage === 'recording' || notice.stage === 'sending') && !notice.opened && document.visibilityState === 'visible' && Date.now() - notice.detectedAt <= 30_000
  update({ notice: { ...notice, route, targetName: name, targetStatus: none ? 'none' : 'found',
    stage: sent ? 'sent' : notice.stage, opened: notice.opened || open } })
  if (open) onConversation(route)
}
export function bindVoiceControl(onConversation: (route: string) => void): () => void {
  if (!isWakeVoiceAvailable()) return () => undefined
  let disposed = false
  const handles: PluginListenerHandle[] = []
  const add = (event: 'stateChange' | 'conversationReady' | 'wakeDetected', listener: (value: unknown) => void) => {
    void wake.addListener(event, (value) => { if (!disposed) listener(value) }).then((handle) => {
      if (disposed) void handle.remove()
      else handles.push(handle)
    }).catch((error: unknown) => update({ error: error instanceof Error ? error.message : 'Could not observe Android voice control' }))
  }
  add('wakeDetected', acceptWake)
  add('stateChange', (value) => {
    try {
      acceptState(value)
      reconcileWakeNotice()
    } catch (error) { update({ error: error instanceof Error ? error.message : 'Could not read voice state' }) }
  })
  add('conversationReady', (value) => acceptConversation(value, onConversation))
  const refresh = () => { if (document.visibilityState === 'visible') void refreshVoiceControl().catch((error: unknown) => update({ error: error instanceof Error ? error.message : 'Could not read voice state' })) }
  refresh()
  document.addEventListener('visibilitychange', refresh)
  return () => {
    disposed = true
    document.removeEventListener('visibilitychange', refresh)
    for (const handle of handles) void handle.remove()
  }
}

let microphoneUsers = 0
let nativePaused = false
let microphoneChange = Promise.resolve()
function syncMicrophone(): Promise<void> {
  const next = microphoneChange.catch(() => undefined).then(async () => {
    if (microphoneUsers > 0 && !nativePaused) { await wake.suspend(); nativePaused = true }
    else if (microphoneUsers === 0 && nativePaused) { await wake.resume(); nativePaused = false }
  })
  microphoneChange = next
  return next
}
export async function acquireVoiceMicrophone(): Promise<() => void> {
  if (!isWakeVoiceAvailable()) return () => undefined
  microphoneUsers += 1
  try { await syncMicrophone() } catch (error) { microphoneUsers -= 1; throw error }
  let released = false
  return () => {
    if (released) return
    released = true
    microphoneUsers -= 1
    void syncMicrophone().catch((error: unknown) => update({ error: error instanceof Error ? error.message : 'Wake listening could not resume. Reopen AgentChat.' }))
  }
}
